/* Repository path: frontend/src/api/planner.ts */

import type { Answers, PlanProfileResponse } from "../types";
import { request } from "./client";
import {
  validateEstimatePayload,
  validatePlanProfilePayload,
  validateSavedPlanPayload,
} from "./estimate";

export function newIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  if (typeof globalThis.crypto?.getRandomValues !== "function") {
    throw new Error("Secure random values are unavailable in this browser.");
  }

  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

interface SessionResponse {
  session_id: string;
  schema_version: "v2";
  expires_at: string;
}

export interface QuestionnaireResponse {
  schema_version: "v2";
  valid: boolean;
  complete: boolean;
  next: string;
}

interface GenericResponse {
  status: string;
}

interface VerifyResponse {
  public_id?: string;
  plan_id?: string;
}

const STEP_FIELDS: Record<number, string[]> = {
  1: [
    "municipality_context",
    "already_in_netherlands",
    "arrival_date",
    "stay_over_four_months",
    "registration_status",
  ],
  2: ["residence_category", "permit_expiry_date"],
  3: ["activities", "recruited_before_arrival", "business_status"],
  4: ["housing_type", "student_accommodation_facilities", "household_type"],
  5: ["insurance_type", "asset_income_flags", "vehicle_type"],
};

export const createQuestionnaireSession = () =>
  request<SessionResponse>("/questionnaire-sessions", { method: "POST", body: {} });

export const updateQuestionnaire = (sessionId: string, body: Record<string, unknown>) =>
  request<QuestionnaireResponse>(
    `/questionnaire-sessions/${encodeURIComponent(sessionId)}`,
    { method: "PATCH", body: { schema_version: "v2", ...body } },
  );

export const calculatePreview = async (sessionId: string) =>
  validateEstimatePayload(
    await request<unknown>(
      `/questionnaire-sessions/${encodeURIComponent(sessionId)}/preview`,
      { method: "POST", body: {} },
    ),
  );

function apiValuesAndInactive(answers: Answers): {
  values: Record<string, unknown>;
  inactive: Set<string>;
} {
  const values: Record<string, unknown> = {
    municipality_context: answers.municipalityContext,
    already_in_netherlands: answers.alreadyInNetherlands,
    arrival_date: answers.arrivalDate,
    stay_over_four_months: answers.stayOverFourMonths,
    registration_status: answers.registrationStatus,
    residence_category: answers.residenceCategory,
    permit_expiry_date: answers.permitExpiryUnknown ? null : answers.permitExpiryDate,
    activities: answers.activities,
    recruited_before_arrival: answers.recruitedBeforeArrival,
    business_status: answers.businessStatus,
    housing_type: answers.housingType,
    student_accommodation_facilities: answers.studentAccommodationFacilities,
    household_type: answers.householdType,
    insurance_type: answers.insuranceType,
    asset_income_flags: answers.assetIncomeFlags,
    vehicle_type: answers.vehicleType,
  };
  const inactive = new Set<string>();
  if (answers.alreadyInNetherlands !== "yes") inactive.add("registration_status");
  if (!answers.activities.some((activity) => ["employee", "paid_intern"].includes(activity))) {
    inactive.add("recruited_before_arrival");
  }
  if (!answers.activities.includes("freelancer_business_owner")) {
    inactive.add("business_status");
  }
  if (answers.housingType !== "student_accommodation") {
    inactive.add("student_accommodation_facilities");
  }
  return { values, inactive };
}

export const answersToApi = (answers: Answers, step: number): Record<string, unknown> => {
  const { values, inactive } = apiValuesAndInactive(answers);
  return Object.fromEntries(
    (STEP_FIELDS[step] ?? [])
      .filter((field) => !inactive.has(field))
      .filter((field) => values[field] !== undefined && values[field] !== "")
      .map((field) => [field, values[field]]),
  );
};

/**
 * The same field mapping and conditional-inactive rules as `answersToApi`,
 * applied to every field at once rather than one wizard step at a time —
 * what a profile edit (W4-07) submits, since it replaces the whole
 * profile in a single request. A `null` value (an explicitly-unknown
 * permit expiry) is kept rather than filtered out, so that unsetting a
 * previously-known date reaches the server as a real change instead of
 * being silently dropped as "no value to send".
 */
export function fullAnswersToApi(answers: Answers): Record<string, unknown> {
  const { values, inactive } = apiValuesAndInactive(answers);
  return Object.fromEntries(
    Object.keys(values)
      .filter((field) => !inactive.has(field))
      .filter((field) => values[field] !== undefined && values[field] !== "")
      .map((field) => [field, values[field]]),
  );
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

/**
 * The reverse of `answersToApi` — seeds the profile editor (W4-07) from a
 * server-shaped Fast Profile, whether that came from a fresh preview's own
 * `profile` field or from `GET /plans/{id}/profile`. Every field is
 * defensively coerced rather than trusted, since a malformed value here
 * must degrade to "blank", never crash the editor. `permitExpiryUnknown`
 * cannot be reconstructed with certainty — the server drops
 * `permit_expiry_date` for the same reason whether the user ticked
 * "I don't know" or simply left an optional field blank — so this
 * defaults it to `false`, matching the first-run wizard's own default,
 * rather than presuming a checkbox the user may never have ticked.
 */
export function apiToAnswers(profile: Record<string, unknown>): Answers {
  return {
    municipalityContext: asString(profile.municipality_context),
    alreadyInNetherlands: asString(profile.already_in_netherlands),
    arrivalDate: asString(profile.arrival_date),
    stayOverFourMonths: asString(profile.stay_over_four_months),
    registrationStatus: asString(profile.registration_status),
    residenceCategory: asString(profile.residence_category),
    permitExpiryDate: asString(profile.permit_expiry_date),
    permitExpiryUnknown: false,
    activities: asStringArray(profile.activities) as Answers["activities"],
    recruitedBeforeArrival: asString(profile.recruited_before_arrival),
    businessStatus: asString(profile.business_status),
    housingType: asString(profile.housing_type),
    studentAccommodationFacilities: asString(profile.student_accommodation_facilities),
    householdType: asString(profile.household_type),
    insuranceType: asString(profile.insurance_type),
    assetIncomeFlags: asStringArray(profile.asset_income_flags) as Answers["assetIncomeFlags"],
    vehicleType: asString(profile.vehicle_type),
  };
}

export const captureEmail = (previewId: string, email: string, reminderOptIn: boolean) =>
  request<GenericResponse>(`/plans/${encodeURIComponent(previewId)}/email-capture`, {
    method: "POST",
    body: { email, reminder_opt_in: reminderOptIn },
  });

export const verifyEmail = (token: string) =>
  request<VerifyResponse>("/access/verify-email", { method: "POST", body: { token } });

export const recoverAccess = (email: string) =>
  request<GenericResponse>("/access/recovery", { method: "POST", body: { email } });

export const getPlan = async (publicId: string) =>
  validateSavedPlanPayload(
    await request<unknown>(`/plans/${encodeURIComponent(publicId)}`),
  );

async function refine(path: string, inputVersion: string, inputCode: string, value: unknown, idempotencyKey?: string) {
  return validateEstimatePayload(await request<unknown>(path, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey ?? newIdempotencyKey() },
    body: { expected_input_version: inputVersion, input_code: inputCode, value },
  }));
}

export const refineQuestionnaire = (sessionId: string, inputVersion: string, inputCode: string, value: unknown, idempotencyKey?: string) =>
  refine(`/questionnaire-sessions/${encodeURIComponent(sessionId)}/refinements`, inputVersion, inputCode, value, idempotencyKey);

export const refinePlan = (publicId: string, inputVersion: string, inputCode: string, value: unknown, idempotencyKey?: string) =>
  refine(`/plans/${encodeURIComponent(publicId)}/refinements`, inputVersion, inputCode, value, idempotencyKey);

/**
 * `GET /plans/{publicId}/profile` (W4-07) — seeds the profile editor for a
 * saved plan with its current answers and the detail questions a change
 * might re-open, by code and label only, never by value. There is
 * deliberately no preview equivalent: the preview client already holds
 * `preview.profile` from the response that created or last edited it. See
 * docs/architecture/features/W4-07.md section 7.
 */
export const getPlanProfile = async (publicId: string): Promise<PlanProfileResponse> =>
  validatePlanProfilePayload(
    await request<unknown>(`/plans/${encodeURIComponent(publicId)}/profile`),
  );

/**
 * `POST /questionnaire-sessions/{sessionId}/profile-edits` (W4-07) — edits
 * the Fast Profile behind an anonymous preview. Appends a new input
 * version and snapshot to the *same* plan; never creates a second one.
 * `body` is the full, normalized server-shaped Fast Profile, not a
 * partial patch — the editor always submits every field, exactly as the
 * first-run wizard's final step does. Returns the same shape a fresh
 * `calculatePreview` would, plus `profile` and `profile_edit_result`.
 */
export const editSessionProfile = async (
  sessionId: string,
  expectedInputVersion: string,
  body: Record<string, unknown>,
  idempotencyKey?: string,
) =>
  validateEstimatePayload(
    await request<unknown>(
      `/questionnaire-sessions/${encodeURIComponent(sessionId)}/profile-edits`,
      {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey ?? newIdempotencyKey() },
        body: { expected_input_version: expectedInputVersion, schema_version: "v2", profile: body },
      },
    ),
  );

/**
 * `POST /plans/{publicId}/profile-edits` (W4-07) — the saved-plan
 * equivalent of `editSessionProfile`. Returns the full recalculated saved
 * plan, exactly as `saveItemDeadline`/`saveItemActual` do, so the caller
 * can simply replace its local state with the result.
 */
export const editPlanProfile = async (
  publicId: string,
  expectedInputVersion: string,
  body: Record<string, unknown>,
  idempotencyKey?: string,
) =>
  validateSavedPlanPayload(
    await request<unknown>(`/plans/${encodeURIComponent(publicId)}/profile-edits`, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey ?? newIdempotencyKey() },
      body: { expected_input_version: expectedInputVersion, schema_version: "v2", profile: body },
    }),
  );

export const setReminder = (publicId: string, enabled: boolean) =>
  request<GenericResponse>(`/plans/${encodeURIComponent(publicId)}/reminders`, {
    method: enabled ? "POST" : "DELETE",
    body: enabled ? {} : undefined,
  });

/**
 * W4-04: record, correct or clear (`deadline: null`) the date a user has
 * actually been given for one item on a saved plan. `item_code` travels in
 * the body, never the path or a query string — which items a plan carries
 * is profile-derived and inherits the W2-02/W4-03 sensitivity rule. Never
 * called for a preview: the deadline control itself is not offered there.
 * Returns the full recalculated plan, so the caller can simply replace its
 * local state with the result rather than issuing a second `getPlan`. See
 * docs/architecture/features/W4-04.md §6.
 */
export const saveItemDeadline = async (
  publicId: string,
  itemCode: string,
  deadline: string | null,
  idempotencyKey?: string,
) =>
  validateSavedPlanPayload(
    await request<unknown>(`/plans/${encodeURIComponent(publicId)}/item-deadlines`, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey ?? newIdempotencyKey() },
      body: { item_code: itemCode, deadline },
    }),
  );

/**
 * W4-06: record, correct or reverse (`state: "open"`, `actualAmount: null`)
 * what actually happened for one item on a saved plan — an amount, a
 * state, or both, independently of each other. `item_code` travels in the
 * body, never the path or a query string, for the same W2-02/W4-03
 * sensitivity reason `saveItemDeadline` already follows. Never called for
 * a preview: the record control itself is not offered there. Returns the
 * full recalculated plan, so the caller simply replaces its local state
 * with the result. See docs/architecture/features/W4-06.md section
 * "Data/API changes".
 */
export const saveItemActual = async (
  publicId: string,
  itemCode: string,
  state: string,
  actualAmount: string | null,
  idempotencyKey?: string,
) =>
  validateSavedPlanPayload(
    await request<unknown>(`/plans/${encodeURIComponent(publicId)}/item-actuals`, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey ?? newIdempotencyKey() },
      body: { item_code: itemCode, state, actual_amount: actualAmount },
    }),
  );

export const requestDeletion = (publicId: string) =>
  request<GenericResponse>(`/plans/${encodeURIComponent(publicId)}/deletion-request`, {
    method: "POST",
    body: { confirm: true },
  });

/**
 * W5-02: turn one reminder-eligible item's reminders off or back on, under
 * the plan's existing reminder consent. `item_code` travels in the body,
 * never the path or a query string, for the same W2-02/W4-03 sensitivity
 * reason `saveItemDeadline`/`saveItemActual` already follow. Never called
 * for a preview. Returns the full recalculated plan, so the caller simply
 * replaces its local state with the result — no optimistic update happens
 * before this resolves. See docs/architecture/features/W5-02.md §9.
 */
export const saveItemReminder = async (
  publicId: string,
  itemCode: string,
  enabled: boolean,
  idempotencyKey?: string,
) =>
  validateSavedPlanPayload(
    await request<unknown>(`/plans/${encodeURIComponent(publicId)}/item-reminders`, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey ?? newIdempotencyKey() },
      body: { item_code: itemCode, enabled },
    }),
  );

interface StopRemindersResponse {
  status: "reminders_off" | "already_used";
}

/**
 * W5-02: the reminder email's stop link. No session, no plan id in the
 * path or a query string — the single-use token is the only thing that
 * identifies a plan, and this call grants no access beyond turning
 * reminders off. `"already_used"` is a normal, expected outcome (the
 * person already used this exact link, or pressed the button twice) —
 * never render it as an error. A `410`/`stop_link_invalid` response
 * throws via `request`'s own `ApiError`, exactly as every other endpoint's
 * error responses do. See docs/architecture/features/W5-02.md §5.
 */
export const stopRemindersWithLink = (token: string) =>
  request<StopRemindersResponse>("/reminders/unsubscribe", {
    method: "POST",
    body: { token },
  });

export function consumeFragmentToken(): string {
  const token = new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "";
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}${window.location.search}`,
  );
  return token;
}






